import { useMutation, useQuery, useSuspenseQuery } from "@apollo/client";
import {
  AccountCard,
  Button,
  Copyable,
  DataTable,
  DataTableColumnHeader,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  PrimaryFullScreenSpinner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TabLink,
  TabLinkContainer,
  toast,
  useAppForm,
} from "@repo/ui";
import { NotFoundError, sissInstitutions } from "@repo/utils";
import { createFileRoute, useParams } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Suspense, useMemo, useState } from "react";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { OrganisationCard } from "~/components/Organisation/OrganisationCard";
import { graphql } from "~/gql/client";
import type {
  SissAccountFinalisedFragment,
  SissAccountInProgressFragment,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

const tabs = ["in-progress", "finalised"] as const;
type Tab = (typeof tabs)[number];

export const Route = createFileRoute("/__auth/siss-accounts/$tab")({
  head: () => opsHead("SISS Accounts"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const params = useParams({ strict: false });

  let selectedTab: Tab | undefined;
  if (tabs.includes(params["tab"] as Tab)) {
    selectedTab = params["tab"] as Tab;
  } else {
    throw new NotFoundError();
  }

  return (
    <AdminPageContainer>
      <AdminPageHeader
        title="SISS Accounts"
        buttons={<CreateSissAccountButton />}
      />
      <TabLinkContainer className="mb-6">
        <TabLink title="In Progress" to="/siss-accounts/in-progress" />
        <TabLink title="Finalised" to="/siss-accounts/finalised" />
      </TabLinkContainer>
      {selectedTab === "in-progress" ? <InProgressTab /> : <FinalisedTab />}
    </AdminPageContainer>
  );
}

function InProgressTab() {
  const { data, refetch, loading } = useQuery(
    graphql(`
      query SissAccountsInProgress {
        sissExternalAccounts(
          statuses: [CREATED, APPROVAL_SENT_TO_CLIENT, APPROVAL_RECEIVED_BY_SISS]
        ) {
          id
          ...SissAccountInProgress
        }
      }

      fragment SissAccountInProgress on SissExternalAccountDetails {
        id
        name
        sissAccountStatus
        statusReason
        businessEntity {
          id
          name
          organisation {
            id
            name
            squareLogoUrl
          }
        }
        cafDocument {
          id
          filename
          signedUrl
        }
      }
    `),
    { notifyOnNetworkStatusChange: true },
  );

  const accounts = data?.sissExternalAccounts ?? [];
  const onSuccess = useMemo(() => () => void refetch(), [refetch]);

  const inProgressColumns = useMemo(
    (): ColumnDef<SissAccountInProgressFragment>[] => [
      {
        id: "id",
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="ID" />
        ),
        cell: ({ row }) => (
          <span className="font-mono text-tertiary">
            <Copyable copyText={row.original.id}>
              #{row.original.id.slice(-8)}
            </Copyable>
          </span>
        ),
      },
      {
        id: "organisation",
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Organisation" />
        ),
        cell: ({ row }) => {
          const org = row.original.businessEntity?.organisation;
          return org ? (
            <OrganisationCard
              organisation={org}
              linkTo={`/organisations/${org.id}`}
            />
          ) : (
            "—"
          );
        },
      },
      {
        id: "businessEntity",
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Business Entity" />
        ),
        cell: ({ row }) => row.original.businessEntity?.name ?? "—",
      },
      {
        id: "name",
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Account" />
        ),
        cell: ({ row }) => (
          <div className="flex flex-col gap-0.5">
            <span>{row.original.name}</span>
            {row.original.statusReason && (
              <div className="text-tertiary text-xs">
                {row.original.statusReason}
              </div>
            )}
          </div>
        ),
      },
      {
        id: "status",
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Status" />
        ),
        cell: ({ row }) => (
          <span className="text-secondary capitalize">
            {row.original.sissAccountStatus
              ?.toLowerCase()
              .replaceAll("_", " ") ?? "—"}
          </span>
        ),
      },
      {
        id: "actions",
        enableSorting: false,
        header: () => null,
        cell: ({ row }) => (
          <InProgressActions account={row.original} onSuccess={onSuccess} />
        ),
      },
    ],
    [onSuccess],
  );

  return (
    <DataTable
      data={accounts}
      columns={inProgressColumns}
      isLoadingData={loading && !data}
      manualSorting={true}
      pageSize={20}
      className="overflow-hidden border"
    />
  );
}

function InProgressActions({
  account,
  onSuccess,
}: {
  account: SissAccountInProgressFragment;
  onSuccess: () => void;
}) {
  const [cancelSissExternalAccount, { loading: cancelLoading }] = useMutation(
    graphql(`
      mutation CancelSissExternalAccount($input: CancelSissExternalAccountInput!) {
        cancelSissExternalAccount(input: $input) {
          externalAccount {
            id
            sissAccountStatus
          }
        }
      }
    `),
  );

  const [markSubmitted, { loading: markLoading }] = useMutation(
    graphql(`
      mutation MarkSissAccountApprovalSentToClientFromQueue($input: MarkSissAccountApprovalSentToClientInput!) {
        markSissAccountApprovalSentToClient(input: $input) {
          externalAccount {
            id
            sissAccountStatus
          }
        }
      }
    `),
  );

  async function handleCancel() {
    await cancelSissExternalAccount({
      variables: { input: { externalAccountId: account.id } },
    });
    toast({ kind: "success", title: "Account cancelled", autoHide: true });
    onSuccess();
  }

  async function handleMarkSubmitted() {
    await markSubmitted({
      variables: { input: { externalAccountId: account.id } },
    });
    toast({ kind: "success", title: "Marked as submitted", autoHide: true });
    onSuccess();
  }

  const isLoading = cancelLoading || markLoading;

  return (
    <div className="flex items-center justify-end gap-2">
      <Button
        size="sm"
        variant="outline"
        onClick={() => void handleCancel()}
        disabled={isLoading}
        loading={cancelLoading}
      >
        Cancel
      </Button>
      <Button
        size="sm"
        variant="outline"
        disabled={!account.cafDocument}
        onClick={() => {
          if (account.cafDocument) {
            window.open(account.cafDocument.signedUrl, "_blank");
          }
        }}
      >
        Download CAF
      </Button>
      <Button
        size="sm"
        onClick={() => void handleMarkSubmitted()}
        disabled={isLoading || account.sissAccountStatus !== "CREATED"}
        loading={markLoading}
      >
        Mark Submitted
      </Button>
    </div>
  );
}

const finalisedColumns: ColumnDef<SissAccountFinalisedFragment>[] = [
  {
    id: "id",
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="ID" />
    ),
    cell: ({ row }) => (
      <span className="font-mono text-tertiary">
        <Copyable copyText={row.original.id}>
          #{row.original.id.slice(-8)}
        </Copyable>
      </span>
    ),
  },
  {
    id: "organisation",
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Organisation" />
    ),
    cell: ({ row }) => {
      const org = row.original.businessEntity?.organisation;
      return org ? (
        <OrganisationCard
          organisation={org}
          linkTo={`/organisations/${org.id}`}
        />
      ) : (
        "—"
      );
    },
  },
  {
    id: "businessEntity",
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Business Entity" />
    ),
    cell: ({ row }) => row.original.businessEntity?.name ?? "—",
  },
  {
    id: "asset",
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Asset" />
    ),
    cell: ({ row }) => {
      const asset = row.original.asset;
      return asset ? (
        <AccountCard
          variant="DEFAULT"
          account={asset}
          linkTo={`/assets/${asset.id}`}
          subText="PROVIDER"
        />
      ) : (
        "—"
      );
    },
  },
  {
    id: "name",
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Account" />
    ),
    accessorFn: (row) => row.name,
  },
  {
    id: "status",
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => (
      <span className="text-secondary capitalize">
        {row.original.sissAccountStatus?.toLowerCase().replaceAll("_", " ") ??
          "—"}
      </span>
    ),
  },
];

function FinalisedTab() {
  const { data, loading } = useQuery(
    graphql(`
      query SissAccountsFinalised {
        sissExternalAccounts(
          statuses: [ACTIVE, CANCELLED_BY_SISS, DISCONNECTED, CLOSED, PROBLEM]
        ) {
          id
          ...SissAccountFinalised
        }
      }

      fragment SissAccountFinalised on SissExternalAccountDetails {
        id
        name
        sissAccountStatus
        businessEntity {
          id
          name
          organisation {
            id
            name
            squareLogoUrl
          }
        }
        asset {
          ...AssetCardFields
        }
      }
    `),
    { notifyOnNetworkStatusChange: true },
  );

  const accounts = data?.sissExternalAccounts ?? [];

  return (
    <DataTable
      data={accounts}
      columns={finalisedColumns}
      isLoadingData={loading}
      manualSorting={true}
      pageSize={20}
      className="overflow-hidden border"
    />
  );
}

const createSissSchema = z.object({
  organisationId: z.string().min(1, "Required"),
  businessEntityId: z.string().min(1, "Required"),
  institutionId: z.string().min(1, "Required"),
  accountName: z.string().min(1, "Required"),
  accountNumber: z.string().min(1, "Required"),
  bsb: z.string().min(1, "Required"),
  nickname: z.string(),
});

function CreateSissAccountButton() {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="primary">New SISS Account</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle>New SISS Account</DialogTitle>
        <DialogDescription>
          Create a new SISS external account for a business entity.
        </DialogDescription>
        <CreateSissAccountForm onSuccess={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  );
}

function CreateSissAccountForm({ onSuccess }: { onSuccess: () => void }) {
  const { data } = useSuspenseQuery(
    graphql(`
      query SissAccountsOrganisations {
        organisations {
          id
          name
          businessEntities {
            id
            name
          }
        }
      }
    `),
  );

  const [createSissExternalAccount, { loading }] = useMutation(
    graphql(`
      mutation CreateSissExternalAccountFromDialog($input: CreateSissExternalAccountInput!) {
        createSissExternalAccount(input: $input) {
          externalAccount {
            id
            sissAccountStatus
          }
        }
      }
    `),
  );

  const institutionOptions = Object.values(sissInstitutions);
  const organisations = data?.organisations ?? [];

  const form = useAppForm({
    defaultValues: {
      organisationId: "",
      businessEntityId: "",
      institutionId: "",
      accountName: "",
      accountNumber: "",
      bsb: "",
      nickname: "",
    },
    validators: { onChange: createSissSchema },
    onSubmit: async ({ value }) => {
      const result = await createSissExternalAccount({
        variables: {
          input: {
            businessEntityId: value.businessEntityId,
            institutionId: value.institutionId,
            accountName: value.accountName,
            accountNumber: value.accountNumber,
            bsb: value.bsb,
            nickname: value.nickname || undefined,
          },
        },
        onError: (error) => {
          const description =
            error.graphQLErrors?.[0]?.message ??
            error.message ??
            "An unexpected error occurred.";
          toast({
            kind: "error",
            autoHide: false,
            title: "Failed to create SISS account",
            description,
          });
        },
      });
      if (!result.errors) {
        toast({
          kind: "success",
          title: "SISS account created",
          autoHide: true,
        });
        onSuccess();
      }
    },
  });

  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void form.handleSubmit();
      }}
    >
      <form.AppForm>
        <form.AppField name="organisationId">
          {(field) => (
            <div className="grid gap-1.5">
              <Label>Organisation</Label>
              <Select
                value={field.state.value}
                onValueChange={(val) => {
                  const org = organisations.find((o) => o.id === val);
                  if (org?.businessEntities?.length === 1) {
                    form.setFieldValue(
                      "businessEntityId",
                      org.businessEntities[0]!.id,
                    );
                  } else {
                    form.resetField("businessEntityId");
                  }
                  field.handleChange(val);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select organisation" />
                </SelectTrigger>
                <SelectContent>
                  {[...organisations]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((org) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </form.AppField>

        <form.AppField name="businessEntityId">
          {(field) => (
            <form.Subscribe selector={(state) => state.values.organisationId}>
              {(orgId) => {
                const bizEntities = orgId
                  ? (
                      organisations.find((o) => o.id === orgId)
                        ?.businessEntities ?? []
                    ).filter(Boolean)
                  : [];
                return (
                  <div className="grid gap-1.5">
                    <Label>Business Entity</Label>
                    <Select
                      disabled={!orgId}
                      value={field.state.value}
                      onValueChange={field.handleChange}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            orgId
                              ? "Select business entity"
                              : "Select organisation first"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {[...bizEntities]
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((be) => (
                            <SelectItem key={be.id} value={be.id}>
                              {be.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              }}
            </form.Subscribe>
          )}
        </form.AppField>

        <form.AppField name="institutionId">
          {(field) => (
            <div className="grid gap-1.5">
              <Label>Institution</Label>
              <Select
                value={field.state.value}
                onValueChange={field.handleChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select institution" />
                </SelectTrigger>
                <SelectContent>
                  {institutionOptions.map((inst) => (
                    <SelectItem key={inst.id} value={inst.id}>
                      {inst.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </form.AppField>

        <form.AppField name="accountName">
          {(field) => (
            <div className="grid gap-1.5">
              <Label>Account Name</Label>
              <Input
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder="e.g. John Smith"
              />
            </div>
          )}
        </form.AppField>

        <form.AppField name="accountNumber">
          {(field) => (
            <div className="grid gap-1.5">
              <Label>Account Number</Label>
              <Input
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder="e.g. 123456789"
              />
            </div>
          )}
        </form.AppField>

        <form.AppField name="bsb">
          {(field) => (
            <div className="grid gap-1.5">
              <Label>BSB</Label>
              <Input
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder="e.g. 062-000"
              />
            </div>
          )}
        </form.AppField>

        <form.AppField name="nickname">
          {(field) => (
            <div className="grid gap-1.5">
              <Label>Nickname (optional)</Label>
              <Input
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder="e.g. Main Account"
              />
            </div>
          )}
        </form.AppField>

        <Button
          variant="primary"
          type="submit"
          className="justify-self-end"
          loading={loading}
        >
          Create Account
        </Button>
      </form.AppForm>
    </form>
  );
}
